import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authService } from '@/lib/auth/authService';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Lock, Eye, EyeOff, Loader2 } from 'lucide-react';

const passwordSchema = z.object({
  currentPassword: z.string().min(6, 'La contraseña actual debe tener al menos 6 caracteres'),
  newPassword: z.string().min(6, 'La nueva contraseña debe tener al menos 6 caracteres'),
  confirmPassword: z.string().min(6, 'La confirmación de contraseña es obligatoria'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export default function ChangePasswordForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = async (data: PasswordFormData) => {
    setIsLoading(true);

    try {
      const result = await authService.changePassword(
        data.currentPassword,
        data.newPassword
      );

      if (result.success) {
        toast({
          title: '¡Contraseña actualizada!',
          description: 'Tu contraseña ha sido cambiada correctamente',
          variant: 'default',
        });
        reset();
      } else {
        toast({
          title: 'Error',
          description: result.message || 'No se pudo cambiar la contraseña',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado. Inténtalo de nuevo más tarde.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-4">Cambiar contraseña</h3>

      <div className="space-y-1">
        <label className="text-white/90 flex items-center gap-2 text-sm font-medium">
          <Lock className="w-4 h-4 text-[#FCAA0D]" />
          Contraseña actual
        </label>
        <div className="relative">
          <input
            type={showCurrentPassword ? "text" : "password"}
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="******"
            {...register('currentPassword')}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
          >
            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.currentPassword && (
          <p className="text-red-500 text-xs mt-1">{errors.currentPassword.message}</p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-white/90 flex items-center gap-2 text-sm font-medium">
          <Lock className="w-4 h-4 text-[#FCAA0D]" />
          Nueva contraseña
        </label>
        <div className="relative">
          <input
            type={showNewPassword ? "text" : "password"}
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="******"
            {...register('newPassword')}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white"
            onClick={() => setShowNewPassword(!showNewPassword)}
          >
            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.newPassword && (
          <p className="text-red-500 text-xs mt-1">{errors.newPassword.message}</p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-white/90 flex items-center gap-2 text-sm font-medium">
          <Lock className="w-4 h-4 text-[#FCAA0D]" />
          Confirmar nueva contraseña
        </label>
        <input
          type="password"
          className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
          placeholder="******"
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full bg-[#FCAA0D] text-zinc-900 font-bold px-6 py-3 rounded-xl hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2 mt-4"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Actualizando...
          </>
        ) : (
          'Cambiar contraseña'
        )}
      </Button>
    </form>
  );
}
